import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export type Option = string | { label: string; value: string };

interface MultiSelectProps {
  options: Option[];
  selected: string[]; // stores canonical values
  onChange: (selected: string[]) => void;
  label?: string;
}

function getLabel(opt: Option) {
  return typeof opt === "string" ? opt : opt.label;
}

function getValue(opt: Option) {
  return typeof opt === "string" ? opt : opt.value;
}

function getKey(opt: Option, idx: number) {
  const v = getValue(opt);
  const l = getLabel(opt);
  return `${v}__${l}__${idx}`;
}

export function MultiSelect({ options, selected, onChange, label }: MultiSelectProps) {
  const toggleOption = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter((item) => item !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <div className="space-y-8">
      {label && (
        <label className="block text-3xl font-normal text-[#2D2A35] mb-10 tracking-tight text-center leading-tight">
          {label}
        </label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
        {options.map((opt, index) => {
          const optValue = getValue(opt);
          const optLabel = getLabel(opt);
          const isSelected = selected.includes(optValue);

          return (
            <motion.button
              key={getKey(opt, index)}
              type="button"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={() => toggleOption(optValue)}
              whileHover={{ scale: 1.01, y: -2 }}
              whileTap={{ scale: 0.99 }}
              className={`
                relative flex items-center p-6 rounded-2xl border text-left transition-all duration-500
                ${
                  isSelected
                    ? "border-[#9B87C7] bg-[#F8F6FB] shadow-md shadow-[#9B87C7]/5"
                    : "border-[#E8E4EE] bg-white hover:border-[#B5A8D4] hover:bg-[#FDFCFE] hover:shadow-sm"
                }
              `}
            >
              <div
                className={`
                flex-shrink-0 w-6 h-6 rounded-full border mr-5 flex items-center justify-center transition-all duration-500 tactile-texture
                ${isSelected ? "bg-[#9B87C7] border-[#9B87C7]" : "border-[#D4CED9] bg-white"}
              `}
              >
                <motion.div
                  initial={false}
                  animate={{ opacity: isSelected ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Check className="w-3.5 h-3.5 text-white stroke-[2] relative z-10" />
                </motion.div>
              </div>

              <span className={`font-medium text-lg ${isSelected ? "text-[#2D2A35]" : "text-[#6B6575]"}`}>
                {optLabel}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}